/**
 * Modelo del dominio Incidencia, alineado al contrato API §1·B y §1.9.
 *
 * El listado paginado vive en `GET /api/v1/incidencias?proyectoId=…` —
 * el FE filtra por proyecto + estado. El detalle pleno con fotos viene
 * en `/proyectos/:id/integral`, que se usa solo para exports/offline.
 *
 * Los estados son cinco y estables; el FE los usa para badges + filtros
 * fijos. Si el backend devuelve un estado fuera del set, la UI lo
 * imprime como string (defensa).
 */

import { toLocalIsoDay } from '@/shared/lib';

import type { StatusTone } from '@/shared/ui';

export type EstadoIncidencia =
  | 'PendienteAprobacion'
  | 'Abierta'
  | 'EnAtencion'
  | 'EnRevisionParticipante'
  | 'EnVerificacion'
  | 'EnIncumplimiento'
  | 'Cerrada'
  | 'Registrada'
  | 'Rechazada';

export const ESTADOS_INCIDENCIA: ReadonlyArray<EstadoIncidencia> = [
  'PendienteAprobacion',
  'Abierta',
  'EnAtencion',
  'EnRevisionParticipante',
  'EnVerificacion',
  'EnIncumplimiento',
  'Cerrada',
  'Registrada',
  'Rechazada',
];

export const ESTADO_INCIDENCIA_LABEL: Record<EstadoIncidencia, string> = {
  PendienteAprobacion: 'Pendiente de aprobación',
  Abierta: 'Abierta',
  EnAtencion: 'En atención',
  EnRevisionParticipante: 'En revisión del participante',
  EnVerificacion: 'En verificación',
  EnIncumplimiento: 'En incumplimiento',
  Cerrada: 'Cerrada',
  Registrada: 'Registrada',
  Rechazada: 'Rechazada',
};

/**
 * Mapeo de estado de incidencia a tono semantico del DS. Coherente con
 * el sistema de tokens existente: success / warning / danger / info /
 * neutral / primary.
 *
 *  - PendienteAprobacion     → warning (espera decisión del supervisor)
 *  - Abierta                 → danger  (impacta, aún sin atender)
 *  - EnAtencion              → info    (el participante la está atendiendo)
 *  - EnRevisionParticipante  → info    (el auxiliar terminó; revisa el líder)
 *  - EnVerificacion          → primary (el líder reportó; verifica el supervisor)
 *  - EnIncumplimiento        → danger  (venció el plazo sin resolverse)
 *  - Cerrada                 → success (resuelta y verificada)
 *  - Registrada              → neutral (registro/sanción terminal, sin ciclo de cura — ADR-031)
 *  - Rechazada               → neutral (descartada con motivo)
 */
export const ESTADO_INCIDENCIA_TONE: Record<EstadoIncidencia, StatusTone> = {
  PendienteAprobacion: 'warning',
  Abierta: 'danger',
  EnAtencion: 'info',
  EnRevisionParticipante: 'info',
  EnVerificacion: 'primary',
  EnIncumplimiento: 'danger',
  Cerrada: 'success',
  Registrada: 'neutral',
  Rechazada: 'neutral',
};

/**
 * Color de gráfico por estado — ESPEJO del `ESTADO_COLORES` del backend
 * (`estadisticas-incidencias.domain-service.ts`): el backend lo aplica a las
 * SERIES cuando se segmenta por estado; el FE usa este mapa cuando el estado
 * es la CATEGORÍA (donut/barras), para que el mismo estado tenga el mismo
 * color en cualquier gráfico. Semántica: Cerrada verde, EnIncumplimiento
 * rojo, Rechazada/Registrada grises (terminales neutras).
 */
export const ESTADO_INCIDENCIA_CHART_COLOR: Record<EstadoIncidencia, string> = {
  PendienteAprobacion: '#F59E0B',
  Abierta: '#2158F3',
  EnAtencion: '#0EA5E9',
  EnRevisionParticipante: '#8B5CF6',
  EnVerificacion: '#29ABE2',
  Cerrada: '#16A34A',
  Rechazada: '#64748B',
  EnIncumplimiento: '#DC2626',
  Registrada: '#94A3B8',
};

/**
 * Recibe `string` por defensa: si el backend agrega un estado nuevo
 * antes de que el contrato lo documente, se muestra tal cual y el tono
 * cae a `neutral`. Una vez que el set se estabilice, los call sites se
 * pueden estrechar a `EstadoIncidencia`.
 */
export function estadoIncidenciaLabel(estado: string): string {
  return (ESTADO_INCIDENCIA_LABEL as Record<string, string>)[estado] ?? estado;
}

export function estadoIncidenciaTone(estado: string): StatusTone {
  return (ESTADO_INCIDENCIA_TONE as Record<string, StatusTone>)[estado] ?? 'neutral';
}

/** Origen del reporte de una incidencia. */
export type OrigenIncidencia = 'Web' | 'Mobile';

export const ORIGENES_INCIDENCIA: ReadonlyArray<OrigenIncidencia> = ['Web', 'Mobile'];

export const ORIGEN_INCIDENCIA_LABEL: Record<OrigenIncidencia, string> = {
  Web: 'Web',
  Mobile: 'Móvil',
};

export function origenIncidenciaLabel(origen: string): string {
  return (ORIGEN_INCIDENCIA_LABEL as Record<string, string>)[origen] ?? origen;
}

/**
 * Ordenamiento del listado (`GET /api/v1/incidencias?ordenarPor=&orden=`).
 * `ordenarPor` admite tres campos de fecha; `orden` la dirección. El backend
 * defaultea `reportadaEn` + `desc` (más recientes primero) cuando se omiten.
 */
export type OrdenarPorIncidencia = 'reportadaEn' | 'fechaLimite' | 'creadoEn';

export const ORDENAR_POR_INCIDENCIA: ReadonlyArray<OrdenarPorIncidencia> = [
  'reportadaEn',
  'fechaLimite',
  'creadoEn',
];

export const ORDENAR_POR_INCIDENCIA_LABEL: Record<OrdenarPorIncidencia, string> = {
  reportadaEn: 'Creada',
  fechaLimite: 'Fecha límite',
  creadoEn: 'Registrada en el sistema',
};

/**
 * Vocabulario unificado de las dos fechas de alta de una incidencia (la app
 * móvil puede levantarla sin conexión y subirla horas después):
 *  - `reportadaEn` → **«Creada»**: cuándo se levantó en campo.
 *  - `creadoEn`    → **«Registrada en el sistema»**: cuándo llegó al servidor.
 * Nunca «Reportada», «Sincronizada», «Subida» ni «Fecha de reporte».
 */
export const AYUDA_FECHA_CREADA = 'Cuándo se levantó en campo, aunque haya sido sin conexión.';
export const AYUDA_FECHA_REGISTRADA = 'Cuándo llegó al servidor.';

export type OrdenIncidencia = 'asc' | 'desc';

export const ORDENES_INCIDENCIA: ReadonlyArray<OrdenIncidencia> = ['asc', 'desc'];

export const ORDEN_INCIDENCIA_LABEL: Record<OrdenIncidencia, string> = {
  asc: 'Ascendente',
  desc: 'Descendente',
};

export type ItemTipoIncidencia = 'CondicionMinima' | 'IndicadorDesempeno';

/** Modo del plazo de cura sellado en la incidencia (ADR-031). */
export type TipoPlazoSnap = 'Fijo' | 'PorEstudio' | 'SinCura';

/** Unidad del plazo fijo sellado (ADR-031). */
export type UnidadPlazoSnap = 'Minutos' | 'Horas' | 'DiasCalendario';

/**
 * Snapshot del supuesto de incumplimiento congelado en la incidencia al
 * crearla/aprobarla (ADR-031). El catálogo es la fuente viva; la
 * incidencia conserva el plazo vigente al momento del incumplimiento.
 * Todo `null`/legacy en incidencias previas a ADR-031 (`aplicaPlazoCura`
 * defaultea `true`).
 *
 *  - `Fijo`       → `plazoValorSnap` + `plazoUnidadSnap` definidos; al
 *                   aprobar la `fechaLimite` la deriva el backend.
 *  - `PorEstudio` → sin plazo numérico; el Supervisor fija la fecha al aprobar.
 *  - `SinCura`    → `aplicaPlazoCura=false`; al aprobar queda `Registrada`.
 */
export interface IncidenciaSupuestoSnapshot {
  /** FK al supuesto sellado; `null` en incidencias legacy. */
  supuestoIncumplimientoId: number | null;
  supuestoDescripcion: string | null;
  tipoPlazoSnap: TipoPlazoSnap | null;
  plazoValorSnap: number | null;
  plazoUnidadSnap: UnidadPlazoSnap | null;
  /** Derivado server-side: `SinCura` ⇒ false; resto ⇒ true. */
  aplicaPlazoCura: boolean;
}

/** Máximo de fotos activas por incidencia (regla backend). */
export const MAX_FOTOS_INCIDENCIA = 10;

/**
 * Estados en los que el backend permite adjuntar/quitar fotos. Cubre el
 * reporte inicial (PendienteAprobacion, Abierta) y la atención del
 * participante privado, que debe subir la **evidencia de resolución** antes
 * de `reportar-terminada` (EnAtencion). Fuera de ellos el adjunto se bloquea.
 */
export const ESTADOS_INCIDENCIA_ADJUNTABLE: ReadonlyArray<EstadoIncidencia> = [
  'PendienteAprobacion',
  'Abierta',
  'EnAtencion',
];

/**
 * Tipo de foto según el momento del ciclo en que se adjuntó (campo `tipoFoto`
 * del backend, ADR-020). `Reporte` la sube el reportante al crear; el resto el
 * participante privado al resolver, como evidencia de cumplimiento. Se tipa
 * laxo (string) por defensa ante valores nuevos; los conocidos se rotulan con
 * `TIPO_FOTO_LABEL`.
 */
export type TipoFotoIncidencia =
  | 'Reporte'
  | 'EvidenciaResolucion'
  | 'Dictamen'
  | 'VerificacionCampo'
  | 'SustentoProrroga'
  | 'Seguimiento';

export const TIPO_FOTO_LABEL: Record<string, string> = {
  Reporte: 'Evidencia del reporte',
  EvidenciaResolucion: 'Evidencia de resolución',
  Dictamen: 'Evidencia del dictamen',
  VerificacionCampo: 'Evidencia de verificación de campo',
  SustentoProrroga: 'Sustento de solicitud de plazo de cura',
  Seguimiento: 'Evidencia de avance en campo',
};

export function tipoFotoLabel(tipo: string | null | undefined): string | undefined {
  if (!tipo) return undefined;
  return TIPO_FOTO_LABEL[tipo] ?? tipo;
}

/**
 * Referencia a una foto ya subida a storage, lista para adjuntarse a una
 * incidencia (`AttachFotoItem`). Se referencia por `storageKey` —la ruta
 * devuelta por `POST /storage/files`—, nunca por URL. Se usa en `fotos[]`
 * al crear, en el endpoint de adjuntar y en cada item del sync.
 */
export interface AttachFotoItem {
  storageKey: string;
  storageBucket?: string;
  nombreOriginal: string;
  mimeType: string;
  tamanoBytes: number;
  hashSha256?: string;
  anchoPx?: number;
  altoPx?: number;
  /** EXIF: fecha de captura (ISO-8601). */
  capturadaEn?: string;
  /** EXIF: "Make Model". */
  dispositivo?: string;
  /**
   * Ubicación WGS84 (string) DONDE se capturó la foto —GPS del dispositivo /
   * geolocalización del navegador, no el EXIF—. Obligatoria en toda foto y todos
   * los flujos (ADR-026); el backend valida el rango y rechaza sin ella
   * (`400 INCIDENCIA_COORDENADAS_INVALIDAS`).
   */
  latitudCaptura: string;
  longitudCaptura: string;
  /**
   * Rol de la foto en el ciclo (ADR-020). El backend default a `Reporte`; el
   * participante privado DEBE enviar `EvidenciaResolucion` al adjuntar su prueba
   * de cumplimiento, o la foto cae en el carrusel del reporte. La evidencia del
   * dictamen del Supervisor (`Dictamen`, ADR-025) viaja inline en el body de la
   * decisión y el backend le fuerza el tipo.
   */
  tipoFoto?: TipoFotoIncidencia;
  /** Idempotencia: reenviar el mismo no duplica. */
  clientUuid?: string;
}

/** Usuario embebido en autorías/atribuciones del detalle de incidencia. */
export interface UsuarioResumen {
  id: number;
  nombre: string;
  nombreCompleto: string;
  email: string;
}

/** Ítem (condición/indicador) reportado, hidratado en el detalle. */
export interface IncidenciaItemReportado {
  tipo: ItemTipoIncidencia;
  /** Id de la instancia per-proyecto (condición/indicador). */
  id: number;
  /**
   * Id del ítem del catálogo legal (`item_catalogo`). De aquí cuelgan los
   * supuestos de incumplimiento (ADR-031); se usa para listarlos al corregir el
   * supuesto en la aprobación (`GET /catalogos/items/:itemCatalogoId/supuestos`).
   */
  itemCatalogoId: number;
  codigo: string;
  texto: string;
  /**
   * Ventana `[inicio, fin]` de la subfase dueña del ítem (ISO-8601). Acota los
   * plazos de la incidencia en el cliente: fecha de cierre al aprobar, fecha de
   * fin de trabajo al reportar terminado y nueva fecha al solicitar prórroga.
   * El backend la incluye en el detalle para que el participante privado valide
   * sin pedir el árbol del proyecto.
   */
  subfaseFechaInicio: string;
  subfaseFechaFinEstimada: string;
}

/**
 * Foto adjunta a una incidencia, tal como llega en el detalle
 * (`GET /api/v1/incidencias/:id`).
 *
 * `contenidoUrl` (`/incidencias/:id/fotos/:fotoId/contenido`) es la ruta de
 * descarga que **respeta la visibilidad por rol** y funciona para todos —usarla
 * siempre (ver `fetchBlobAutenticado`). `url` (`/storage/files/...`) sigue
 * existiendo solo para roles internos; el participante privado ya no tiene
 * `storage:ver`, por eso quedó como compatibilidad y puede venir `null`.
 */
export interface IncidenciaFoto {
  id: number;
  incidenciaId: number;
  /** Descarga con visibilidad por rol — preferida en todo el FE. */
  contenidoUrl: string;
  /** Compatibilidad para roles internos (`/storage/files/...`); puede ser null. */
  url: string | null;
  /** Momento del ciclo en que se adjuntó (reporte vs. resolución). */
  tipoFoto: TipoFotoIncidencia | null;
  /**
   * Ciclo de reporte de cumplimiento al que quedó sellada la foto (ADR-023).
   * Permite agrupar la evidencia de resolución por cada intento del histórico.
   * `null` para fotos del reporte o evidencia aún no sellada a un reporte.
   */
  reporteCumplimientoId: number | null;
  /**
   * Dictamen del Supervisor al que quedó sellada la foto (ADR-025). Permite
   * agrupar su evidencia (`tipoFoto='Dictamen'`) por cada decisión
   * (aprobar/rechazar cumplimiento, incumplimiento). `null` para el resto.
   */
  dictamenId: number | null;
  /**
   * Verificación de campo del Auxiliar a la que quedó sellada la foto
   * (ADR-044). Agrupa la evidencia `tipoFoto='VerificacionCampo'` por cada
   * verificación, igual que `dictamenId` agrupa la del dictamen. Cuando un
   * dictamen consume la verificación, el backend además sella `dictamenId` en
   * estas fotos (pasan a ser evidencia del dictamen). Al participante privado
   * le llegan como `tipoFoto='Dictamen'` con este campo en `null` — nunca ve
   * el flujo interno. `null` para el resto de fotos.
   */
  verificacionCampoId: number | null;
  /**
   * Rechazo del Supervisor que OBSERVÓ esta foto de `Reporte` (ADR-039,
   * selección explícita al rechazar la incidencia). Una foto con `rechazoId`
   * deja de ser "vigente": es trazabilidad interna de la revisión — se agrupa
   * en el historial junto al motivo de ese rechazo y sale de la galería
   * principal. Al participante privado el backend ni se la entrega.
   * `null` = foto vigente.
   */
  rechazoId: number | null;
  /**
   * Solicitud de prórroga a la que quedó sellada la foto
   * (`tipoFoto='SustentoProrroga'`, sustento fotográfico; puede convivir con el
   * PDF en la misma solicitud, ADR-050).
   * Opcional por drift: los despliegues del backend sin la migración aún no lo
   * envían. `null`/ausente para el resto de fotos.
   */
  ampliacionSolicitudId?: number | null;
  /**
   * Avance de campo al que quedó sellada la foto (`tipoFoto='Seguimiento'`,
   * ADR-045). Agrupa la evidencia de cada visita del histórico paralelo, igual
   * que `verificacionCampoId` agrupa la de la verificación. **Bitácora interna
   * de ANI y punto**: a diferencia de `VerificacionCampo`, ningún dictamen la
   * promueve — al participante privado estas fotos no le llegan nunca y el
   * binario le responde `404`. Opcional por drift: los despliegues del backend
   * sin la migración aún no lo envían. `null`/ausente para el resto de fotos.
   */
  seguimientoId?: number | null;
  nombreOriginal: string;
  mimeType: string;
  tamanoBytes: number;
  anchoPx: number | null;
  altoPx: number | null;
  hashSha256: string | null;
  capturadaEn: string | null;
  dispositivo: string | null;
  /** Ubicación WGS84 donde se capturó la foto (ADR-026). Siempre presente. */
  latitudCaptura: string;
  longitudCaptura: string;
  orden: number;
  clientUuid: string | null;
  subidoPorId: number;
  subidoPor: UsuarioResumen | null;
  subidoEn: string;
}

/**
 * ¿La foto está vigente (no observada en ningún rechazo, ADR-039)?
 * `undefined` (backend sin el campo aún) cuenta como vigente — así los
 * despliegues no sincronizados no vacían la galería.
 */
export function esFotoVigente(foto: Pick<IncidenciaFoto, 'rechazoId'>): boolean {
  return foto.rechazoId == null;
}

/**
 * Detalle pleno de `GET /api/v1/incidencias/:id`: incidencia con FKs reales,
 * autorías hidratadas (objeto usuario por cada transición), el ítem reportado
 * y el arreglo `fotos[]`. Distinto de `Incidencia` (item de listado) y de
 * `IncidenciaDetalle` (respuesta liviana del POST de creación).
 */
export interface IncidenciaCompleta extends IncidenciaSupuestoSnapshot {
  id: number;
  codigo: string;
  /** Correlativo oficial por proyecto (ADR-043). `null` hasta aprobarse. */
  correlativo: number | null;
  /** Código oficial legible (`RI-SUP-AEPQ-42-2026`, ADR-043). `null` hasta aprobarse. */
  codigoOficial: string | null;
  proyectoId: number;
  proyecto: { id: number; nombre: string } | null;
  condicionMinimaId: number | null;
  indicadorDesempenoId: number | null;
  itemReportado: IncidenciaItemReportado | null;
  titulo: string;
  descripcion: string;
  estado: EstadoIncidencia;
  motivoRechazo: string | null;
  /** Motivo del último rechazo de cumplimiento o de revisión del participante. */
  motivoRechazoCumplimiento: string | null;
  /** Plazo de cierre fijado al aprobar (ISO-8601). `null` hasta aprobarse. */
  fechaLimite: string | null;
  reportadaPorId: number;
  reportadaPor: UsuarioResumen | null;
  reportadaEn: string;
  aprobadaPorId: number | null;
  aprobadaPor: UsuarioResumen | null;
  aprobadaEn: string | null;
  rechazadaPorId: number | null;
  rechazadaPor: UsuarioResumen | null;
  rechazadaEn: string | null;
  tomadaPorId: number | null;
  tomadaPor: UsuarioResumen | null;
  tomadaEn: string | null;
  /** Quién reportó el trabajo terminado (PP líder o auxiliar) y cuándo. */
  reportadaTerminadaPorId: number | null;
  reportadaTerminadaPor: UsuarioResumen | null;
  reportadaTerminadaEn: string | null;
  /** Fecha real de fin de trabajo declarada al reportar terminado (ISO-8601). */
  fechaFinTrabajo: string | null;
  /** Comentario del participante al reportar el trabajo terminado. */
  comentarioTerminado: string | null;
  /**
   * Plazo del último reporte de terminado (ver `PlazoReporte`). Opcional por
   * drift (backend previo a ADR-051 no lo envía).
   */
  plazoReporteTerminado?: PlazoReporte | null;
  resueltaPorId: number | null;
  resueltaPor: UsuarioResumen | null;
  resueltaEn: string | null;
  incumplidaPorId: number | null;
  incumplidaPor: UsuarioResumen | null;
  incumplidaEn: string | null;
  actualizadoPorId: number | null;
  actualizadoPor: UsuarioResumen | null;
  latitud: string | null;
  longitud: string | null;
  /**
   * Dirección textual del punto reportado (máx 500). Independiente de las
   * coordenadas GPS: puede venir una sin la otra. `null` si no se capturó.
   */
  direccion: string | null;
  origen: 'Web' | 'Mobile';
  deviceId: string | null;
  clientUuid: string | null;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
  fotos: IncidenciaFoto[];
  /** Historial completo de rechazos embebido (desc por `rechazadaEn`). */
  rechazos: IncidenciaRechazo[];
  /** Historial completo de prórrogas embebido. */
  ampliaciones: IncidenciaAmpliacion[];
}

/**
 * Forma minima del item del listado `GET /api/v1/incidencias`. Espeja
 * `IncidenciaListItemResponseDto` del backend. El detalle pleno (incluye
 * fotos) llega via `GET /api/v1/incidencias/:id` (`IncidenciaCompleta`).
 */
export interface Incidencia {
  id: number;
  codigo: string;
  /** Correlativo oficial por proyecto (ADR-043). `null` hasta aprobarse. */
  correlativo: number | null;
  /** Código oficial legible (`RI-SUP-AEPQ-42-2026`, ADR-043). `null` hasta aprobarse. */
  codigoOficial: string | null;
  titulo: string;
  descripcion: string;
  estado: EstadoIncidencia;
  motivoRechazo: string | null;
  itemTipo: ItemTipoIncidencia;
  itemId: number;
  reportadaPorId: number;
  reportadaEn: string;
  aprobadaPorId: number | null;
  /** Cuándo el supervisor aprobó el reporte (ISO-8601). `null` sin aprobar. */
  aprobadaEn: string | null;
  rechazadaPorId: number | null;
  rechazadaEn: string | null;
  tomadaPorId: number | null;
  tomadaEn: string | null;
  resueltaPorId: number | null;
  resueltaEn: string | null;
  /** Plazo de cierre vigente (ISO-8601). `null` hasta aprobarse. */
  fechaLimite: string | null;
  /**
   * Plazo del ÚLTIMO reporte de terminado (ver `PlazoReporte`). Opcional por
   * drift (backend previo a ADR-051 no lo envía).
   */
  plazoReporteTerminado?: PlazoReporte | null;
  latitud: string | null;
  longitud: string | null;
  /** Dirección textual del punto reportado. `null` si no se capturó. */
  direccion: string | null;
  origen: 'Web' | 'Mobile';
  creadoEn: string;
}

/**
 * Toda incidencia lleva DOS identificadores (ADR-043): el folio temporal de
 * recepción `codigo` (`INC-2026-00042`, se asigna al crear y es inmutable) y el
 * código oficial por proyecto `codigoOficial` (`RI-SUP-AEPQ-42-2026`), que el
 * backend asigna en la misma transacción de la aprobación (`aprobar` y
 * `registrar` consumen número; las rechazadas nunca).
 *
 * **Regla de display**: todo lo user-facing muestra `codigoOficial ?? codigo`.
 * Los read models que devuelven un solo campo `codigo` (pines del mapa,
 * backlog de reportes, proyecto integral) ya aplican el coalesce server-side.
 */
export function codigoVisibleIncidencia(inc: {
  codigo: string;
  codigoOficial: string | null;
}): string {
  return inc.codigoOficial ?? inc.codigo;
}

/**
 * ¿La incidencia sigue identificada solo por su folio de recepción? `true`
 * mientras no se apruebe (PendienteAprobacion/Rechazada): la UI puede
 * acompañar el folio con un badge "provisional" para avisar que el número
 * oficial llega con la aprobación.
 */
export function esCodigoProvisional(inc: { codigoOficial: string | null }): boolean {
  return inc.codigoOficial == null;
}

/**
 * Resumen de ubicación COMPACTO (cards mobile): la dirección textual si
 * existe; si no, las coordenadas "lat, lng"; `null` sin ubicación. Las tablas
 * desktop no lo usan: ahí dirección y coordenadas van en columnas separadas
 * (`direccion` + `coordenadasIncidencia`).
 */
export function ubicacionIncidenciaResumen(inc: {
  direccion: string | null;
  latitud: string | null;
  longitud: string | null;
}): string | null {
  if (inc.direccion) return inc.direccion;
  return coordenadasIncidencia(inc);
}

/**
 * Coordenadas GPS "lat, lng" del punto reportado; `null` sin par completo (la
 * celda imprime "—"). Independientes de la `direccion` textual: puede venir
 * una sin la otra.
 */
export function coordenadasIncidencia(inc: {
  latitud: string | null;
  longitud: string | null;
}): string | null {
  if (inc.latitud != null && inc.longitud != null) return `${inc.latitud}, ${inc.longitud}`;
  return null;
}

/** Estados donde la `fechaLimite` sigue corriendo (el vencimiento importa). */
const ESTADOS_PLAZO_ACTIVO: ReadonlySet<string> = new Set([
  'Abierta',
  'EnAtencion',
  'EnRevisionParticipante',
  'EnVerificacion',
  'EnIncumplimiento',
]);

/**
 * ¿El plazo vigente ya venció sin resolverse? Resalta el vencimiento en los
 * listados. Cerrada/Rechazada/Registrada no cuentan: su plazo dejó de correr.
 */
export function esIncidenciaVencida(inc: { estado: string; fechaLimite: string | null }): boolean {
  if (!inc.fechaLimite || !ESTADOS_PLAZO_ACTIVO.has(inc.estado)) return false;
  return new Date(inc.fechaLimite).getTime() < Date.now();
}

/**
 * Fecha en que se **notificó** la incidencia al participante privado (oficio
 * 090 de ANI): el correo «Acción requerida» sale cuando el supervisor la
 * aprueba, así que es `aprobadaEn`. Excepción: una incidencia `Registrada`
 * (supuesto `SinCura`) se aprueba solo para sanción y NO se notifica al PP →
 * `null` (la UI muestra «—»). Único punto de verdad para la columna/fila
 * «Notificada».
 */
export function fechaNotificadaDe(inc: {
  estado: string;
  aprobadaEn: string | null;
}): string | null {
  if (inc.estado === 'Registrada') return null;
  return inc.aprobadaEn ?? null;
}

/**
 * Desfase (en minutos) a partir del cual la hora de campo (declarada por el
 * cliente, p. ej. capturada offline) y la de llegada al servidor se muestran
 * por separado (badge «Sin conexión», ver `MomentoAccion`). Bajo el umbral es
 * solo latencia de red.
 */
export const SINCRONIZACION_DESFASE_MIN = 2;

/**
 * ¿La hora de llegada al servidor (`servidor`) se separa de la hora de campo
 * (`campo`) más que `SINCRONIZACION_DESFASE_MIN`? Con cualquiera de las dos
 * ausente o inválida → `false` (no hay nada que distinguir).
 */
export function esSincronizacionDiferida(
  campo: string | null | undefined,
  servidor: string | null | undefined,
): boolean {
  if (!campo || !servidor) return false;
  const c = new Date(campo).getTime();
  const r = new Date(servidor).getTime();
  if (Number.isNaN(c) || Number.isNaN(r)) return false;
  return Math.abs(r - c) > SINCRONIZACION_DESFASE_MIN * 60_000;
}

/**
 * Respuesta plena de `POST /api/v1/incidencias` (`IncidenciaResponseDto`).
 * A diferencia de `Incidencia` (item de listado, referencia el ítem por
 * `itemTipo`/`itemId`), aquí el backend expone las FKs reales
 * (`condicionMinimaId` / `indicadorDesempenoId`, una en `null`), las
 * coordenadas y los timestamps completos del ciclo de vida.
 *
 * El estado inicial lo decide el backend según el rol del reporter:
 * Auxiliar → `PendienteAprobacion`; Supervisor/Admin/SuperAdmin → `Abierta`.
 */
export interface IncidenciaDetalle extends IncidenciaSupuestoSnapshot {
  id: number;
  codigo: string;
  /** Correlativo oficial por proyecto (ADR-043). `null` hasta aprobarse. */
  correlativo: number | null;
  /** Código oficial legible (`RI-SUP-AEPQ-42-2026`, ADR-043). `null` hasta aprobarse. */
  codigoOficial: string | null;
  proyectoId: number;
  condicionMinimaId: number | null;
  indicadorDesempenoId: number | null;
  titulo: string;
  descripcion: string;
  estado: EstadoIncidencia;
  motivoRechazo: string | null;
  motivoRechazoCumplimiento: string | null;
  /** Plazo de cierre fijado al aprobar (ISO-8601). `null` hasta aprobarse. */
  fechaLimite: string | null;
  reportadaPorId: number;
  reportadaEn: string;
  aprobadaPorId: number | null;
  aprobadaEn: string | null;
  rechazadaPorId: number | null;
  rechazadaEn: string | null;
  tomadaPorId: number | null;
  tomadaEn: string | null;
  reportadaTerminadaPorId: number | null;
  reportadaTerminadaEn: string | null;
  fechaFinTrabajo: string | null;
  comentarioTerminado: string | null;
  /** Plazo del último reporte de terminado. Opcional por drift. */
  plazoReporteTerminado?: PlazoReporte | null;
  resueltaPorId: number | null;
  resueltaEn: string | null;
  incumplidaPorId: number | null;
  incumplidaEn: string | null;
  latitud: string | null;
  longitud: string | null;
  /** Dirección textual del punto reportado. `null` si no se capturó. */
  direccion: string | null;
  origen: 'Web' | 'Mobile';
  deviceId: string | null;
  clientUuid: string | null;
  activo: boolean;
  actualizadoPorId: number | null;
  creadoEn: string;
  actualizadoEn: string;
  /**
   * Fotos adjuntas (presente si se enviaron en `fotos[]`). El shape exacto
   * del item de respuesta aún no está documentado; se tipa laxo porque hoy
   * no se consume (el flujo solo usa `id`/`codigo`/`estado`).
   */
  fotos?: ReadonlyArray<unknown>;
}

/**
 * Fila del historial de prórrogas (`GET /api/v1/incidencias/:id/ampliaciones`,
 * `IncidenciaAmpliacionResponseDto`). Cada registro documenta el salto de plazo
 * (fecha anterior → nueva, **con hora**), si el plazo previo ya había vencido,
 * **cómo** se computó el nuevo (modo plazo numérico vs. fecha exacta), el motivo,
 * el correo notificado y la autoría. El backend devuelve `registradaPorId` sin
 * hidratar el usuario.
 *
 * Cómo leer la línea de tiempo de plazos (contrato prórrogas §5.1):
 *  - `fechaLimiteAnterior` = instante exacto en que vencía el plazo previo
 *    (`null` defensivo).
 *  - `vencidaAnterior` = ese plazo ya había vencido al ampliar (`true` =
 *    recuperación de un incumplimiento; `false` = extensión preventiva).
 *  - `plazoValor` + `plazoUnidad` + `baseComputo` = cómo se otorgó en modo
 *    **plazo numérico** (regla B5); los tres `null` cuando se fijó por **fecha
 *    exacta**.
 */
export interface IncidenciaAmpliacion {
  id: number;
  incidenciaId: number;
  /** Instante en que vencía el plazo previo (ISO-8601). `null` defensivo. */
  fechaLimiteAnterior: string | null;
  /** ¿El plazo previo ya había vencido al otorgar esta prórroga? */
  vencidaAnterior: boolean;
  fechaLimiteNueva: string;
  /** Snapshot del plazo numérico otorgado; `null` si fue por fecha exacta. */
  plazoValor: number | null;
  /** Unidad del plazo numérico; `null` si fue por fecha exacta. */
  plazoUnidad: UnidadPlazoSnap | null;
  /** Instante base desde el que se contó el plazo; `null` si fue por fecha exacta. */
  baseComputo: string | null;
  motivo: string | null;
  notificadoEmail: string | null;
  registradaPorId: number;
  registradaEn: string;
}

/**
 * Fila del historial de rechazos (`GET /api/v1/incidencias/:id/rechazos`,
 * `IncidenciaRechazoResponseDto`). Cada vez que un supervisor rechaza se agrega
 * una entrada; si la incidencia se reenvía y se vuelve a rechazar, el historial
 * conserva todas. El backend devuelve `rechazadaPorId` sin hidratar (el nombre
 * del último rechazo sí viene en el detalle como `rechazadaPor`).
 */
export interface IncidenciaRechazo {
  id: number;
  incidenciaId: number;
  motivo: string;
  rechazadaPorId: number;
  /** Hora de la acción (de campo si el rechazo se hizo offline). */
  rechazadaEn: string;
  /**
   * Llegada del rechazo al servidor. Opcional por drift: el backend lo está
   * agregando a `IncidenciaRechazoResponseDto` y aún no figura en el schema
   * generado. Ausente → solo se muestra la hora de la acción.
   */
  registradoEn?: string | null;
}

/**
 * Tipos de evento de la bitácora de dominio (`GET /incidencias/:id/eventos`,
 * ADR-021 E2/E6). El catálogo lo fija el backend; se rotula con
 * `TIPO_EVENTO_LABEL` y se tipa laxo (string) por defensa ante valores nuevos.
 */
export type TipoEventoIncidencia =
  | 'INICIADA'
  | 'APROBADA'
  | 'RECHAZADA'
  | 'REENVIADA'
  | 'REPORTE_TERMINADO'
  | 'REVISION_PP_APROBADA'
  | 'REVISION_PP_RECHAZADA'
  | 'CUMPLIMIENTO_APROBADO'
  | 'CUMPLIMIENTO_RECHAZADO'
  | 'CERRADA'
  | 'REABIERTA'
  | 'INCUMPLIMIENTO'
  | 'PRORROGA_SOLICITADA'
  | 'PRORROGA_APROBADA'
  | 'PRORROGA_RECHAZADA';

export const TIPO_EVENTO_LABEL: Record<string, string> = {
  INICIADA: 'Atención iniciada',
  APROBADA: 'Reporte aprobado',
  RECHAZADA: 'Reporte rechazado',
  REENVIADA: 'Reporte reenviado',
  REPORTE_TERMINADO: 'Trabajo reportado como terminado',
  REVISION_PP_APROBADA: 'Revisión del participante aprobada',
  REVISION_PP_RECHAZADA: 'Revisión del participante rechazada',
  CUMPLIMIENTO_APROBADO: 'Cumplimiento aprobado',
  CUMPLIMIENTO_RECHAZADO: 'Cumplimiento rechazado',
  CERRADA: 'Incidencia cerrada',
  REABIERTA: 'Incidencia reabierta',
  INCUMPLIMIENTO: 'Marcada en incumplimiento',
  PRORROGA_SOLICITADA: 'Plazo de cura solicitado',
  PRORROGA_APROBADA: 'Plazo de cura aprobado',
  PRORROGA_RECHAZADA: 'Plazo de cura rechazado',
};

export function tipoEventoLabel(tipo: string): string {
  return TIPO_EVENTO_LABEL[tipo] ?? tipo;
}

/**
 * Entrada de la bitácora de una incidencia (`IncidenciaEventoResponseDto`).
 * Línea de tiempo append-only: cada transición agrega un evento con el actor,
 * el salto de estado y metadata específica. El backend devuelve `actorId` sin
 * hidratar; cuando hidrata el actor, llega en `actor`.
 */
export interface IncidenciaEvento {
  id: number;
  incidenciaId: number;
  tipo: TipoEventoIncidencia;
  actorId: number | null;
  actor?: UsuarioResumen | null;
  actorRol: string | null;
  estadoDesde: EstadoIncidencia | null;
  estadoHacia: EstadoIncidencia | null;
  metadata: Record<string, unknown> | null;
  /** Hora del hecho (de campo cuando la acción se hizo offline). */
  ocurridoEn: string;
  /**
   * Llegada del evento al servidor. Opcional por drift (backend sin el campo
   * aún no lo envía); ver `esSincronizacionDiferida`.
   */
  registradoEn?: string | null;
}

/**
 * Resultado de un reporte de cumplimiento (ADR-023). `null` mientras el
 * intento está pendiente de resolución (en verificación o en revisión).
 */
export type ResultadoReporteCumplimiento = 'Aprobado' | 'Rechazado';

/** Clave de presentación del resultado, con el caso pendiente explícito. */
export type EstadoReporteCumplimiento = ResultadoReporteCumplimiento | 'Pendiente';

export const RESULTADO_REPORTE_LABEL: Record<EstadoReporteCumplimiento, string> = {
  Pendiente: 'Pendiente de revisión',
  Aprobado: 'Aprobado',
  Rechazado: 'Rechazado',
};

export const RESULTADO_REPORTE_TONE: Record<EstadoReporteCumplimiento, StatusTone> = {
  Pendiente: 'warning',
  Aprobado: 'success',
  Rechazado: 'danger',
};

/** Normaliza `resultado` (string|null del backend) a la clave de presentación. */
export function estadoReporteCumplimiento(resultado: string | null): EstadoReporteCumplimiento {
  if (resultado === 'Aprobado' || resultado === 'Rechazado') return resultado;
  return 'Pendiente';
}

export function resultadoReporteLabel(resultado: string | null): string {
  return RESULTADO_REPORTE_LABEL[estadoReporteCumplimiento(resultado)];
}

export function resultadoReporteTone(resultado: string | null): StatusTone {
  return RESULTADO_REPORTE_TONE[estadoReporteCumplimiento(resultado)];
}

/**
 * Plazo de un reporte de terminado del participante (ADR-051 del backend): su
 * hora de CAMPO frente a la fecha límite vigente al llegar, al milisegundo. El
 * plazo nunca bloquea el reporte; esto solo ANOTA y el Supervisor decide.
 *
 *  - `EnPlazo`                  → reportó y llegó a tiempo (lo normal).
 *  - `EnPlazoSincronizadoTarde` → reportó en campo a tiempo, SIN SEÑAL, y llegó
 *    vencido: «Reportado dentro del plazo».
 *  - `FueraDePlazo`             → reportó en campo después del vencimiento.
 *
 * `null` = sin fecha límite (SinCura) o reporte anterior a la anotación.
 */
export type PlazoReporte = 'EnPlazo' | 'EnPlazoSincronizadoTarde' | 'FueraDePlazo';

export const PLAZO_REPORTE_LABEL: Record<PlazoReporte, string> = {
  EnPlazo: 'Reportado en plazo',
  EnPlazoSincronizadoTarde: 'Reportado dentro del plazo',
  FueraDePlazo: 'Reportado fuera de plazo',
};

export const PLAZO_REPORTE_TONE: Record<PlazoReporte, StatusTone> = {
  EnPlazo: 'success',
  EnPlazoSincronizadoTarde: 'success',
  FueraDePlazo: 'warning',
};

/**
 * ¿La anotación merece badge? `EnPlazo` es el caso normal y no se pinta (sería
 * ruido en cada fila); se destacan el reporte en plazo que llegó vencido —el
 * dato que el Supervisor necesita para decidir— y el reporte fuera de plazo.
 * Valores desconocidos (drift) → `false`.
 */
export function plazoReporteDestacable(
  plazo: string | null | undefined,
): plazo is 'EnPlazoSincronizadoTarde' | 'FueraDePlazo' {
  return plazo === 'EnPlazoSincronizadoTarde' || plazo === 'FueraDePlazo';
}

/**
 * Etapa en que se resolvió un reporte de cumplimiento (ADR-023):
 * `RevisionLider` (el PP líder revisó el reporte del auxiliar) o
 * `VerificacionSupervisor` (el supervisor verificó el cumplimiento).
 */
export type EtapaResolucionReporte = 'RevisionLider' | 'VerificacionSupervisor';

export const ETAPA_RESOLUCION_REPORTE_LABEL: Record<string, string> = {
  RevisionLider: 'Revisión del participante líder',
  VerificacionSupervisor: 'Verificación del supervisor',
};

export function etapaResolucionReporteLabel(etapa: string | null): string | undefined {
  if (!etapa) return undefined;
  return ETAPA_RESOLUCION_REPORTE_LABEL[etapa] ?? etapa;
}

/**
 * Una entrada del histórico de reportes de cumplimiento del participante privado
 * (`GET /api/v1/incidencias/:id/reportes-cumplimiento`, ADR-023). Cada vez que el
 * PP reporta el trabajo terminado se agrega una fila inmutable con su comentario,
 * la fecha de fin de trabajo y, al resolverse, el resultado y el motivo del
 * rechazo. A diferencia de la bitácora interna, este histórico SÍ lo ve el PP: es
 * su superficie para saber por qué le rechazaron cada intento y cuándo. Las fotos
 * del intento se obtienen filtrando `IncidenciaFoto.reporteCumplimientoId === id`.
 *
 * ADR-047: con la visibilidad compartida los líderes operan en equipo, así que
 * el autor de cada intento ya no es deducible del detalle (tras un rechazo
 * puede reintentar OTRO líder). Por eso cada fila viaja con sus autorías
 * hidratadas (`reportadaPor` / `resueltoPor`) además de los ids crudos.
 */
export interface IncidenciaReporteCumplimiento {
  id: number;
  incidenciaId: number;
  /** PP (líder o auxiliar) que reportó terminado en este intento. */
  reportadaPorId: number;
  /**
   * Autor hidratado de ESTE intento (ADR-047). Opcional por drift (backend
   * previo a ADR-047 no lo envía); `null` si la identidad está oculta al actor.
   */
  reportadaPor?: UsuarioResumen | null;
  /** Hora en que el PP reportó terminado (de campo si lo hizo sin conexión). */
  reportadaEn: string;
  /**
   * Llegada del reporte al servidor; posterior a `reportadaEn` si el móvil lo
   * sincronizó después. Opcional por drift (backend sin el campo no lo envía).
   */
  registradoEn?: string | null;
  /** Comentario del participante al reportar el trabajo terminado. */
  comentario: string;
  /** Fecha real de fin de trabajo declarada (ISO-8601). */
  fechaFinTrabajo: string;
  /** `EnVerificacion` (lo reportó el líder) o `EnRevisionParticipante` (auxiliar). */
  estadoDestino: string;
  /**
   * Plazo de ESTE intento (ver `PlazoReporte`). Opcional por drift; `null` sin
   * fecha límite o en intentos anteriores a la anotación.
   */
  plazoReporte?: PlazoReporte | null;
  /**
   * Fecha límite vigente cuando llegó el intento (snapshot contra el que se
   * midió `plazoReporte`; una prórroga posterior no la cambia).
   */
  fechaLimiteAlReportar?: string | null;
  /** Si la incidencia estaba en incumplimiento al llegar el intento, cuándo se marcó. */
  incumplidaEnAlReportar?: string | null;
  /**
   * `true` si el Supervisor marcó el incumplimiento DESPUÉS de que el PP
   * reportó en campo (el reporte venía en la cola del móvil).
   */
  incumplimientoPosteriorAlReporte?: boolean;
  /** Resolución del intento: `Aprobado` | `Rechazado`. `null` si sigue pendiente. */
  resultado: ResultadoReporteCumplimiento | null;
  /** Etapa en que se resolvió. `null` mientras está pendiente. */
  etapaResolucion: EtapaResolucionReporte | null;
  /** Motivo del rechazo del intento (si fue rechazado). */
  motivoRechazo: string | null;
  resueltoPorId: number | null;
  /**
   * Quien resolvió el intento, hidratado (ADR-047): el Supervisor en
   * `VerificacionSupervisor`, el PP líder en `RevisionLider`. Opcional por
   * drift; `null` mientras el intento sigue pendiente.
   */
  resueltoPor?: UsuarioResumen | null;
  resueltoEn: string | null;
  /** Cuándo el PP líder subió el reporte del auxiliar al supervisor (intermedio). */
  revisadoPorLiderEn: string | null;
}

/**
 * Acción de un dictamen del Supervisor (ADR-025). Cada decisión sobre el trabajo
 * reportado por el participante: verificó y cerró, lo devolvió a atención, o
 * marcó incumplimiento. Se tipa laxo (string en el consumo) por defensa.
 */
export type AccionDictamen = 'aprobarCumplimiento' | 'rechazarCumplimiento' | 'incumplir';

export const ACCION_DICTAMEN_LABEL: Record<string, string> = {
  aprobarCumplimiento: 'Cumplimiento aprobado',
  rechazarCumplimiento: 'Cumplimiento rechazado',
  incumplir: 'Incumplimiento',
};

export const ACCION_DICTAMEN_TONE: Record<string, StatusTone> = {
  aprobarCumplimiento: 'success',
  rechazarCumplimiento: 'danger',
  incumplir: 'danger',
};

export function accionDictamenLabel(accion: string | null | undefined): string {
  if (!accion) return 'Dictamen';
  return ACCION_DICTAMEN_LABEL[accion] ?? accion;
}

export function accionDictamenTone(accion: string | null | undefined): StatusTone {
  if (!accion) return 'neutral';
  return ACCION_DICTAMEN_TONE[accion] ?? 'neutral';
}

/**
 * Una decisión del Supervisor sobre el trabajo reportado
 * (`GET /api/v1/incidencias/:id/dictamenes`, ADR-025). Más reciente primero.
 * Su evidencia (comentario + fotos) NO viene embebida: las fotos se obtienen
 * filtrando `IncidenciaFoto.dictamenId === id`. El vínculo con el intento del PP
 * que resolvió es `reporteCumplimientoId` (`null` en incumplimiento: no hubo
 * reporte previo, el dictamen va suelto en la línea de tiempo). Este histórico
 * SÍ lo ve el PP: es su superficie para entender con qué prueba le
 * aprobaron/rechazaron.
 */
export interface IncidenciaDictamen {
  id: number;
  incidenciaId: number;
  /** Reporte del PP que este dictamen resolvió; `null` en incumplimiento. */
  reporteCumplimientoId: number | null;
  accion: AccionDictamen;
  estadoDesde: string;
  estadoHacia: string;
  comentario: string;
  /** El Supervisor que dictaminó (solo el id; el detalle hidrata los nombres). */
  dictaminadoPorId: number;
  /** Hora del dictamen (de campo si se hizo sin conexión). */
  dictaminadoEn: string;
  /** Llegada del dictamen al servidor. Opcional por drift. */
  registradoEn?: string | null;
  origen: OrigenIncidencia;
}

/**
 * Estado del ciclo propio de una verificación de campo del Auxiliar (ADR-044).
 * La máquina de estados de la incidencia NO cambia; este ciclo vive aparte:
 *
 *  - `Pendiente` → espera al Supervisor (una sola pendiente a la vez).
 *  - `Atendida`  → un dictamen la consumió (`dictamenId` set).
 *  - `Observada` → el Supervisor la devolvió con motivo; el Auxiliar reintenta.
 *  - `Superada`  → el reporte del PP se resolvió por otra vía; sin efecto.
 */
export type EstadoVerificacionCampo = 'Pendiente' | 'Atendida' | 'Observada' | 'Superada';

export const ESTADO_VERIFICACION_CAMPO_LABEL: Record<EstadoVerificacionCampo, string> = {
  Pendiente: 'Pendiente de dictamen',
  Atendida: 'Consumida por un dictamen',
  Observada: 'Devuelta al auxiliar',
  Superada: 'Superada',
};

export const ESTADO_VERIFICACION_CAMPO_TONE: Record<EstadoVerificacionCampo, StatusTone> = {
  Pendiente: 'warning',
  Atendida: 'success',
  Observada: 'danger',
  Superada: 'neutral',
};

/** Laxo (string) por defensa ante estados nuevos, como el resto del dominio. */
export function estadoVerificacionCampoLabel(estado: string): string {
  return (ESTADO_VERIFICACION_CAMPO_LABEL as Record<string, string>)[estado] ?? estado;
}

export function estadoVerificacionCampoTone(estado: string): StatusTone {
  return (ESTADO_VERIFICACION_CAMPO_TONE as Record<string, StatusTone>)[estado] ?? 'neutral';
}

/**
 * Verificación de campo del Auxiliar interno (ADR-044,
 * `GET /api/v1/incidencias/:id/verificaciones-campo`, más reciente primero).
 * Con la incidencia `EnVerificacion`, el Auxiliar constata en sitio lo que el
 * PP hizo (comentario + ≥1 foto) para que el Supervisor pueda dictaminar a
 * distancia consumiendo esa evidencia (`verificacionCampoId` en el dictamen) o
 * devolverla con motivo (`Observada`). Sus fotos se obtienen del `fotos[]` del
 * detalle filtrando por `verificacionCampoId`.
 *
 * **Flujo interno ANI**: el Participante Privado recibe `[]` en el histórico
 * (mismo criterio que `/eventos`) y nunca sabe que existió — si un dictamen la
 * consume, ve esas fotos como evidencia del dictamen, sin autor.
 */
export interface IncidenciaVerificacionCampo {
  id: number;
  incidenciaId: number;
  /** Reporte del PP que esta verificación constata. */
  reporteCumplimientoId: number | null;
  /** Lo que el Auxiliar constató en campo. */
  comentario: string;
  estado: EstadoVerificacionCampo;
  /** Auxiliar que verificó en campo (solo el id, sin hidratar). */
  verificadoPorId: number;
  /** Hora de la constatación en campo (puede ser offline). */
  verificadoEn: string;
  /** Llegada de la verificación al servidor. Opcional por drift. */
  registradoEn?: string | null;
  observadaPorId: number | null;
  observadaEn: string | null;
  /** Motivo con que el Supervisor la devolvió al Auxiliar. */
  motivoObservacion: string | null;
  /** Dictamen que la consumió (`Atendida`). */
  dictamenId: number | null;
  origen: OrigenIncidencia;
}

/**
 * Mapa `usuarioId → nombreCompleto` armado con las autorías ya hidratadas en el
 * detalle (reportante, quien aprobó, rechazó, tomó, reportó el trabajo,
 * resolvió o marcó incumplimiento).
 *
 * Los históricos secundarios (rechazos, dictámenes, avances de campo) traen
 * solo el id de su autor: este mapa cubre el caso usual —el supervisor del
 * proyecto (1:1) firma casi todas las transiciones— sin pedir usuarios extra.
 * Si un id no está, el caller cae a un texto neutro en vez de inventar nombres.
 *
 * ADR-047: con varios PP líderes operando en equipo el detalle ya no alcanza
 * para nombrar a todos los autores. `extra` permite sumar las autorías que SÍ
 * vienen hidratadas por fila (intentos de cumplimiento, solicitudes de
 * prórroga), de modo que los históricos que solo traen ids —dictámenes,
 * verificaciones— resuelvan más nombres.
 */
export function nombresActoresIncidencia(
  inc: {
    reportadaPor?: UsuarioResumen | null;
    aprobadaPor?: UsuarioResumen | null;
    rechazadaPor?: UsuarioResumen | null;
    tomadaPor?: UsuarioResumen | null;
    reportadaTerminadaPor?: UsuarioResumen | null;
    resueltaPor?: UsuarioResumen | null;
    incumplidaPor?: UsuarioResumen | null;
  },
  extra: ReadonlyArray<UsuarioResumen | null | undefined> = [],
): Map<number, string> {
  const map = new Map<number, string>();
  const actores = [
    inc.reportadaPor,
    inc.aprobadaPor,
    inc.rechazadaPor,
    inc.tomadaPor,
    inc.reportadaTerminadaPor,
    inc.resueltaPor,
    inc.incumplidaPor,
    ...extra,
  ];
  for (const u of actores) {
    if (u) map.set(u.id, u.nombreCompleto);
  }
  return map;
}

/**
 * Un **avance de campo** registrado por el equipo interno de ANI mientras el
 * participante privado atiende la incidencia (ADR-045,
 * `GET /api/v1/incidencias/:id/seguimientos`, más reciente primero **por
 * `capturadoEn`**).
 *
 * Es un histórico **paralelo**: no aprueba, no rechaza, no mueve el estado ni
 * la fecha límite. Documenta que ANI dio seguimiento —la visita diaria durante
 * los días que el participante tarda— y sirve como evidencia probatoria. Es
 * **append-only**: no se edita ni se borra. Sus fotos se obtienen del `fotos[]`
 * del detalle filtrando por `seguimientoId`.
 *
 * **Flujo interno ANI, permanente**: el Participante Privado recibe `[]`, no ve
 * las fotos y nunca se entera de que existe (a diferencia de ADR-044, no hay
 * camino por el que se vuelvan visibles).
 */
export interface IncidenciaSeguimiento {
  id: number;
  incidenciaId: number;
  /** Lo que se constató en la visita. */
  comentario: string;
  /** Dónde estaba quien registró (WGS84 string). Distinta de la de cada foto. */
  latitud: string;
  longitud: string;
  /** Dirección textual del punto visitado; complementa las coordenadas. */
  direccion: string | null;
  /**
   * Instante REAL de la visita, declarado por el cliente. El histórico ordena
   * por este campo, no por `registradoEn`: tres visitas de días distintos
   * sincronizadas juntas conservan su cronología.
   */
  capturadoEn: string;
  /**
   * Estado de la incidencia **al registrar** el avance (snapshot congelado: si
   * la incidencia avanza después, este valor no cambia). Permite leer el
   * histórico sabiendo en qué momento del ciclo se tomó cada visita. Se tipa
   * laxo (string) por defensa ante estados nuevos, como el resto del dominio:
   * `estadoIncidenciaLabel`/`Tone` ya caen al valor crudo si no lo conocen.
   */
  estadoIncidencia: string;
  /** Quién registró el avance (solo el id, sin hidratar). */
  registradoPorId: number;
  /**
   * Autor hidratado por el backend (`registradoPor` en el DTO). Viene resuelto
   * desde la consulta porque los dos roles que consumen este histórico
   * —Auxiliar y Supervisor— NO tienen `usuarios:listar` para traducir el id.
   * Se declara opcional solo para tolerar despliegues del backend anteriores a
   * ADR-045; `autorSeguimiento` cubre ese hueco.
   */
  registradoPor?: SeguimientoAutor | null;
  /**
   * Cuándo lo recibió el backend; posterior a `capturadoEn` si vino offline.
   * Opcional por drift, como el resto de horas de llegada.
   */
  registradoEn?: string | null;
  origen: OrigenIncidencia;
}

/**
 * Autor de un avance tal como lo entrega el backend
 * (`IncidenciaActorResponseDto`). `nombreCompleto` es nullable en el contrato
 * —no todo usuario lo tiene cargado—, por eso se cae a `nombre`.
 */
export interface SeguimientoAutor {
  id: number;
  nombre: string;
  nombreCompleto: string | null;
  email: string;
}

/** Cómo se resolvió el nombre del autor de un avance (define el copy en la UI). */
export type OrigenAutorSeguimiento = 'hidratado' | 'actor' | 'desconocido';

export interface AutorSeguimiento {
  /** Texto a mostrar. Nunca vacío: en el peor caso identifica por id. */
  etiqueta: string;
  origen: OrigenAutorSeguimiento;
}

/**
 * Resuelve **quién registró** un avance de campo, que es lo que convierte el
 * histórico en evidencia atribuible.
 *
 * El backend lo entrega hidratado (`registradoPor`), resuelto en la propia
 * consulta: los roles que leen esta bitácora —Auxiliar y Supervisor— no tienen
 * `usuarios:listar`, así que no podrían traducir `registradoPorId` por su
 * cuenta. Las dos ramas siguientes existen solo como red de seguridad para un
 * backend anterior a ADR-045, y **nunca muestran «usted»**: el punto del
 * registro es decir QUIÉN estuvo en el sitio, y «usted» no identifica a nadie
 * para quien lea el expediente después.
 */
export function autorSeguimiento(
  seguimiento: Pick<IncidenciaSeguimiento, 'registradoPorId' | 'registradoPor'>,
  contexto: { nombrePorId?: Map<number, string> } = {},
): AutorSeguimiento {
  const autor = seguimiento.registradoPor;
  const hidratado = autor?.nombreCompleto?.trim() || autor?.nombre?.trim();
  if (hidratado) return { etiqueta: hidratado, origen: 'hidratado' };

  const delMapa = contexto.nombrePorId?.get(seguimiento.registradoPorId)?.trim();
  if (delMapa) return { etiqueta: delMapa, origen: 'actor' };

  return { etiqueta: `Usuario #${seguimiento.registradoPorId}`, origen: 'desconocido' };
}

/**
 * Desfase (en minutos) a partir del cual se considera que el avance se
 * **sincronizó en diferido**: se capturó en campo y llegó al servidor después.
 * Bajo este umbral la diferencia es solo latencia de red y no vale la pena
 * mostrar dos fechas.
 */
const SINCRONIZACION_DIFERIDA_MIN = 15;

/**
 * ¿El avance llegó al servidor bastante después de la visita? Es la señal de
 * que se capturó offline (o se registró desde el escritorio días después): la
 * UI muestra entonces ambas fechas ("visita del 4 jun · sincronizado el 6 jun")
 * para dejar claro que el registro es de campo, no de oficina.
 */
export function esSeguimientoSincronizadoDiferido(s: {
  capturadoEn: string;
  registradoEn?: string | null;
}): boolean {
  if (!s.registradoEn) return false;
  const capturado = new Date(s.capturadoEn).getTime();
  const registrado = new Date(s.registradoEn).getTime();
  if (Number.isNaN(capturado) || Number.isNaN(registrado)) return false;
  return registrado - capturado > SINCRONIZACION_DIFERIDA_MIN * 60_000;
}

/** Día local `YYYY-MM-DD` de un instante; `''` si la fecha es inválida. */
function diaLocal(iso: string): string {
  return toLocalIsoDay(iso);
}

/** Días calendario entre dos días locales `YYYY-MM-DD` (`desde` ≤ `hasta`). */
function diasEntre(desde: string, hasta: string): number {
  const a = new Date(`${desde}T00:00:00`).getTime();
  const b = new Date(`${hasta}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** Una jornada de campo: todas las visitas de un mismo día local. */
export interface DiaSeguimiento {
  /** Día local `YYYY-MM-DD`. */
  dia: string;
  /** Visitas de ese día, de la más reciente a la más antigua. */
  items: IncidenciaSeguimiento[];
  /**
   * Días calendario **sin ninguna visita** entre este día y el inmediatamente
   * anterior con actividad. `0` en jornadas consecutivas y en la más antigua.
   * Es el hueco de cobertura que la línea de tiempo señala: expone los lapsos
   * en que nadie fue al sitio, que es justo lo que un histórico de seguimiento
   * debe hacer visible.
   */
  huecoDias: number;
}

/**
 * Agrupa el histórico de avances (desc por `capturadoEn`) en **jornadas de
 * campo** y calcula el hueco de días sin visita entre jornadas consecutivas.
 * Agrupar por día es lo que convierte una lista plana en una cronología legible
 * ("el martes fueron dos veces; luego pasaron tres días sin ir").
 *
 * Tolera entradas desordenadas: reordena por `capturadoEn` descendente antes de
 * agrupar, de modo que un `[]` del backend, un orden distinto o una mezcla de
 * avances sincronizados en diferido no rompen la lectura.
 */
export function agruparSeguimientosPorDia(items: IncidenciaSeguimiento[]): DiaSeguimiento[] {
  const ordenados = [...items].sort(
    (a, b) => new Date(b.capturadoEn).getTime() - new Date(a.capturadoEn).getTime(),
  );

  const dias: DiaSeguimiento[] = [];
  for (const item of ordenados) {
    const dia = diaLocal(item.capturadoEn);
    const ultimo = dias[dias.length - 1];
    if (ultimo && ultimo.dia === dia) ultimo.items.push(item);
    else dias.push({ dia, items: [item], huecoDias: 0 });
  }

  // El hueco se mide contra la jornada ANTERIOR en el tiempo, que en orden
  // descendente es la siguiente del array.
  for (let i = 0; i < dias.length - 1; i++) {
    const actual = dias[i]!;
    const previa = dias[i + 1]!;
    if (!actual.dia || !previa.dia) continue;
    actual.huecoDias = Math.max(0, diasEntre(previa.dia, actual.dia) - 1);
  }

  return dias;
}

/** Cifras de cabecera del histórico de avances. `null` cuando no hay ninguno. */
export interface ResumenSeguimientos {
  /** Total de visitas registradas. */
  total: number;
  /** Jornadas distintas con al menos una visita. */
  diasConVisita: number;
  /** `capturadoEn` de la visita más reciente. */
  ultimaVisitaEn: string | null;
  /** `capturadoEn` de la primera visita. */
  primeraVisitaEn: string | null;
}

/**
 * Resume el histórico para la banda de cifras del apartado: cuántas visitas,
 * en cuántas jornadas distintas y cuándo fue la primera y la última. Se calcula
 * sobre `capturadoEn` (la fecha real de campo), nunca sobre `registradoEn`.
 */
export function resumenSeguimientos(items: IncidenciaSeguimiento[]): ResumenSeguimientos {
  if (items.length === 0)
    return { total: 0, diasConVisita: 0, ultimaVisitaEn: null, primeraVisitaEn: null };

  const tiempos = items
    .map((s) => new Date(s.capturadoEn).getTime())
    .filter((t) => !Number.isNaN(t));
  const dias = new Set(items.map((s) => diaLocal(s.capturadoEn)).filter(Boolean));

  return {
    total: items.length,
    diasConVisita: dias.size,
    ultimaVisitaEn: tiempos.length > 0 ? new Date(Math.max(...tiempos)).toISOString() : null,
    primeraVisitaEn: tiempos.length > 0 ? new Date(Math.min(...tiempos)).toISOString() : null,
  };
}

/**
 * Estados en los que la incidencia está **viva** y admite registrar un avance
 * de campo (ADR-045 §2). Fuera de ellos el backend responde `409
 * INCIDENCIA_NO_ADMITE_SEGUIMIENTO` (definitivo). `Cerrada` queda fuera a
 * propósito: solo la cola offline del móvil puede colar un avance capturado
 * mientras la incidencia seguía viva.
 */
export const ESTADOS_INCIDENCIA_CON_SEGUIMIENTO: ReadonlyArray<EstadoIncidencia> = [
  'Abierta',
  'EnAtencion',
  'EnRevisionParticipante',
  'EnVerificacion',
  'EnIncumplimiento',
];

/** ¿El estado actual admite registrar un avance de campo desde la web? */
export function admiteSeguimiento(estado: string): boolean {
  return (ESTADOS_INCIDENCIA_CON_SEGUIMIENTO as ReadonlyArray<string>).includes(estado);
}

/** Estado de una solicitud de prórroga del participante privado. */
export type EstadoSolicitudAmpliacion = 'Pendiente' | 'Aprobada' | 'Rechazada';

export const ESTADO_SOLICITUD_AMPLIACION_LABEL: Record<EstadoSolicitudAmpliacion, string> = {
  Pendiente: 'Pendiente',
  Aprobada: 'Aprobada',
  Rechazada: 'Rechazada',
};

export const ESTADO_SOLICITUD_AMPLIACION_TONE: Record<EstadoSolicitudAmpliacion, StatusTone> = {
  Pendiente: 'warning',
  Aprobada: 'success',
  Rechazada: 'neutral',
};

/**
 * Solicitud de prórroga del participante privado líder
 * (`IncidenciaAmpliacionSolicitudResponseDto`). Lleva el **sustento** —PDF
 * (referenciado por `documentoStorageKey`), fotos (`fotos[]`) o ambos
 * (ADR-050; al menos uno)—, la fecha sugerida y, una vez resuelta,
 * la autoría y el comentario del supervisor. Solo puede haber una `Pendiente`
 * a la vez por incidencia.
 *
 * ADR-047: cualquier PP líder asignado puede solicitar la prórroga (ya no solo
 * quien tomó la incidencia), así que `solicitadaPor` viaja hidratado — con el
 * equipo operando en turnos, quién pidió cada prórroga no es deducible.
 */
export interface IncidenciaAmpliacionSolicitud {
  id: number;
  incidenciaId: number;
  solicitadaPorId: number;
  /** PP líder que pidió ESTA prórroga (hidratado, ADR-047). */
  solicitadaPor?: UsuarioResumen | null;
  motivo: string;
  /** `null` cuando la solicitud no adjuntó PDF (solo fotos). */
  documentoStorageKey: string | null;
  documentoStorageBucket: string | null;
  /**
   * Fotos de sustento (`tipoFoto='SustentoProrroga'`); `[]` cuando la solicitud
   * no adjuntó fotos (solo PDF). Opcional por drift: los despliegues del backend sin la
   * migración aún no lo envían. Verlas siempre por `contenidoUrl` (el PP no
   * tiene acceso al storage genérico).
   */
  fotos?: IncidenciaFoto[];
  fechaLimiteSugerida: string;
  estado: EstadoSolicitudAmpliacion;
  resueltaPorId: number | null;
  /** Supervisor que resolvió la solicitud (hidratado). `null` si `Pendiente`. */
  resueltaPor?: UsuarioResumen | null;
  resueltaEn: string | null;
  comentarioResolucion: string | null;
  motivoRechazo: string | null;
  ampliacionId: number | null;
  /**
   * Instante en que el PP reportó el trabajo terminado con esta solicitud aún
   * `Pendiente` (ADR-021 ter): la constancia de que no se quedó esperando la
   * resolución. `null` si no aplica. Opcional por drift (backend sin la
   * migración aún no lo envía).
   */
  trabajoReportadoEn?: string | null;
  /**
   * `true` solo cuando `estado='Aprobada'` Y la aprobación realmente movió la
   * `fechaLimite` (ADR-021 ter). Una `Aprobada` con `false` quedó como
   * constancia (el PP ya había reportado). Opcional por drift; ver
   * `esProrrogaAplicada` para leerla con fallback.
   */
  aplicadaAFechaLimite?: boolean;
  /**
   * Hora en que el PP hizo la solicitud en campo (puede ser anterior a la
   * llegada si se capturó offline). Opcional por drift: backend sin el campo
   * no lo envía → usar `fechaSolicitudAmpliacion`.
   */
  solicitadaEn?: string | null;
  /** Llegada de la solicitud al servidor. */
  creadoEn: string;
}

/** Fecha visible de una solicitud de prórroga: la de campo, o la de llegada. */
export function fechaSolicitudAmpliacion(
  s: Pick<IncidenciaAmpliacionSolicitud, 'solicitadaEn' | 'creadoEn'>,
): string {
  return s.solicitadaEn ?? s.creadoEn;
}

/**
 * ¿La solicitud aprobada movió realmente la `fechaLimite`? (ADR-021 ter). Con
 * backend sin el campo (`aplicadaAFechaLimite` ausente) se deriva de
 * `ampliacionId`: la aprobación operativa siempre crea la ampliación.
 */
export function esProrrogaAplicada(
  s: Pick<IncidenciaAmpliacionSolicitud, 'aplicadaAFechaLimite' | 'ampliacionId'>,
): boolean {
  return s.aplicadaAFechaLimite ?? s.ampliacionId != null;
}

/** Bucket para los PDF de sustento de prórroga (el backend lo acota a PDF ≤2 MB). */
export const STORAGE_BUCKET_DOCUMENTOS_INCIDENCIAS = 'documentos-incidencias';

/**
 * Tamaño máximo del PDF de sustento de prórroga (ADR-050): límite propio del
 * bucket `documentos-incidencias`, menor que el general del storage. Mayor →
 * 413 `FILE_TOO_LARGE` al subir o 400
 * `INCIDENCIA_SOLICITUD_AMPLIACION_DOCUMENTO_DEMASIADO_GRANDE` al solicitar.
 */
export const MAX_BYTES_DOCUMENTO_SUSTENTO_PRORROGA = 2 * 1024 * 1024;

/**
 * Máximo de fotos de sustento por solicitud de prórroga (cota del backend,
 * `ArrayMaxSize(5)` en `SolicitarAmpliacionDto`). Cupo propio, menor que el
 * de la galería de la incidencia (`MAX_FOTOS_INCIDENCIA`).
 */
export const MAX_FOTOS_SUSTENTO_PRORROGA = 5;

/**
 * Regla del plazo de cura: el PDF de sustento es obligatorio siempre; las
 * fotos solo lo complementan, nunca lo reemplazan.
 */
export const MENSAJE_PDF_OBLIGATORIO_PLAZO_CURA =
  'Para el plazo de cura es obligatorio adjuntar un documento en PDF aunque se agregue fotografía.';
